"use client";
import React, { useCallback, useState } from "react";
import Image from "next/image";
import FundSearchModal from "../modal/FundSearchModal";
import { FundComparisonResult, SearchedFunds } from "@/type/schemeType";
import Icon from "../icons/Icons";
import { ROOT_URL } from "@/utils/gloableVariables";

type Props = {
  fund: FundComparisonResult;
};

type returns = {
  three_year?: number | string | null;
  one_year?: number | string | null;
  one_month?: number | string | null;
};

export default function FundForComparisonCard({ fund }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [bottomFund, setBottomFund] = useState<SearchedFunds | null>(null); 

  const topReturns = {
    three_year: fund.scheme_first_three_year,
    one_year: fund.scheme_first_one_year,
    one_month: fund.scheme_first_one_month,
  };

  const bottomReturns = {
    three_year: bottomFund?.three_year,
    one_year: bottomFund?.one_year_abs,
    one_month: bottomFund?.one_month_abs,
  };

  const getReturnPeriod = () => {
    const top = {
      three_year: typeof topReturns.three_year === "number" && topReturns.three_year !== 0,
      one_year: typeof topReturns.one_year === "number" && topReturns.one_year !== 0,
      one_month: typeof topReturns.one_month === "number" && topReturns.one_month !== 0,
    };

    const bottom = {
      three_year: typeof bottomReturns?.three_year === "number" && bottomReturns.three_year !== 0,
      one_year: typeof bottomReturns?.one_year === "number" && bottomReturns.one_year !== 0,
      one_month: typeof bottomReturns?.one_month === "number" && bottomReturns.one_month !== 0,
    };

    if (!bottomFund) {
      if (top.three_year) return { key: "three_year", label: "3Y Returns", unit: "p.a." };
      if (top.one_year) return { key: "one_year", label: "1Y Returns", unit: "p.a." };
      if (top.one_month) return { key: "one_month", label: "1M Returns", unit: "p.m." };
      return { key: "one_month", label: "N/A", unit: "" };
    }

    if (top.three_year && bottom.three_year) return { key: "three_year", label: "3Y Returns", unit: "p.a." };
    if (top.one_year && bottom.one_year) return { key: "one_year", label: "1Y Returns", unit: "p.a." };
    if (top.one_month && bottom.one_month) return { key: "one_month", label: "1M Returns", unit: "p.m." };

    return { key: "one_month", label: "N/A", unit: "" };
  };

  const { key: returnKey, label: returnLabel, unit } = getReturnPeriod();

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!fund) return null;

  const cardContent = (
    <>
      <div className={`border-2 border-[#1A51AA1A] rounded-2xl p-3 w-full max-w-65 shrink-0 bg-[#F5F9FF] ${bottomFund ? "" : "group"} transition duration-350 hover:border-[#1A51AA5C]`}>
        {/* Top Fund */}
        <div className="flex gap-2 items-start h-14.5">
          <span className="rounded-full w-7 h-7 bg-[#F8F8F8] shrink-0 inline-block mt-1 overflow-hidden">
            <Image src={`${ROOT_URL}/img/AMCsLogo/${fund.first_amc_code}.png`} alt={fund.scheme_first_name} width={30} height={30} className="object-contain mb-1" unoptimized />
          </span>
          <div>
            <p className="text-sm leading-4 text-left text-primaryBlack font-medium line-clamp-2 transition duration-350 group-hover:text-primaryBlue">{fund.scheme_first_name}</p>

            <p className="text-sm leading-4 text-[#444444] mt-2">
              {returnLabel}:{" "}
              <span className="text-[#1F840D] font-bold">
                {(topReturns as returns)[returnKey as keyof returns] ?? 0} ({unit})
              </span>
            </p>
          </div>
        </div>

        {/* VS divider */}
        <div className="my-3 h-8 flex items-center justify-center">
          <div className="w-full border-t border-dashed border-primaryBlue relative">
            <span className="absolute w-8 h-8 inline-flex items-center justify-center top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-medium text-primaryBlue bg-white border border-primaryBlue rounded-full">VS</span>
          </div>
        </div>

        {/* Bottom Fund or Add Button */}
        {bottomFund ? (
          <div className="flex gap-2 items-start h-14.5">
            <span className="rounded-full w-7 h-7 bg-[#F8F8F8] shrink-0 inline-block mt-1 overflow-hidden">
              <Image src={`${ROOT_URL}/img/AMCsLogo/${bottomFund.amc_code}.png`} alt={bottomFund.scheme_name} width={30} height={30} className="object-contain mb-1" unoptimized />
            </span>
            <div>
              <p className="text-sm leading-4 text-left text-primaryBlack font-medium line-clamp-2 transition duration-350 group-hover:text-primaryBlue">{bottomFund.scheme_name}</p>

              <p className="text-sm leading-4 text-[#444444] mt-2">
                {returnLabel}:{" "}
                <span className="text-[#1F840D] font-bold">
                  {(bottomReturns as returns)[returnKey as keyof returns] ?? 0} ({unit})
                </span>
              </p>
            </div>
          </div>
        ) : (
          <button onClick={() => setIsOpen(true)} className="group/child flex items-center justify-center gap-2 h-15 w-full rounded-xl bg-white text-base text-[#6E6E73] hover:bg-gray-100 hover:text-primaryBlue transition duration-350">
            <Icon.addPlusIcon className="text-[#6E6E73] group-hover/child:text-primaryBlue transition" />
            Add Fund
          </button>
        )}
      </div>

      <FundSearchModal isOpen={isOpen} onClose={handleClose} onSelect={(searchedfund) => setBottomFund(searchedfund)} />
    </>
  );

  return bottomFund ? (
    <a href={`${ROOT_URL}mutual-fund/compare/${bottomFund.url}-vs-${fund.scheme_first_url}`} className="w-full max-w-65 shrink-0 overflow-hidden rounded-2xl group transition duration-350 hover:border-[#1A51AA5C] xs:hover:shadow-[0px_4px_8px_0px_#00000020] xs:hover:-translate-y-2">
      {cardContent}
    </a>
  ) : (
    cardContent
  );
}
